import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import IconSVG from './IconSVG';
import Button from './Button';

export interface ShareOption {
  id: string;
  label: string;
  icon: string;
  action: () => void;
}

export interface ShareOptionsMenuProps {
  options: ShareOption[];
  className?: string;
  buttonLabel?: string;
  buttonVariant?: 'primary' | 'secondary' | 'outlined' | 'text';
  buttonSize?: 'sm' | 'md' | 'lg';
  buttonIcon?: string;
  title?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  testId?: string;
}

const ShareOptionsMenu: React.FC<ShareOptionsMenuProps> = ({
  options,
  className = '',
  buttonLabel = 'Share',
  buttonVariant = 'primary',
  buttonSize = 'md',
  buttonIcon = 'share',
  title = 'Share options',
  placement = 'bottom',
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: ShareOption) => {
    option.action();
    setIsOpen(false);
  };

  // Determine menu position classes based on placement
  const getMenuPositionClasses = () => {
    switch (placement) {
      case 'top':
        return 'bottom-full mb-2';
      case 'left':
        return 'right-full mr-2';
      case 'right':
        return 'left-full ml-2';
      case 'bottom':
      default:
        return 'top-full mt-2';
    }
  };

  return (
    <div className={classNames('relative inline-block', className)} data-testid={testId}>
      <Button
        ref={buttonRef}
        onClick={toggleMenu}
        variant={buttonVariant}
        size={buttonSize}
        leftIcon={<IconSVG icon={buttonIcon} />}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : 'false'}
      >
        {buttonLabel}
      </Button>
      
      {isOpen && (
        <div
          ref={menuRef}
          className={classNames(
            'absolute z-10 w-60 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
            getMenuPositionClasses()
          )}
        >
          {title && (
            <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</h3>
            </div>
          )}
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.id}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => handleOptionClick(option)}
              >
                <IconSVG icon={option.icon} className="mr-3 h-5 w-5 text-gray-500 dark:text-gray-400" />
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareOptionsMenu;